import { useCallback, useState, useRef } from "react";
import { useDropzone } from "react-dropzone";
import { BiCloudUpload, BiX } from "react-icons/bi";
import SecondaryButton from "./SecondaryButton";

const FileComponent = ({
  title,
  subtitle,
  acceptedFileTypes = "all",
  maxFiles = 1,
  onChange,
}) => {
  const [files, setFiles] = useState([]);
  const fileInputRef = useRef(null);

  // Determine accepted file types for react-dropzone
  const getAcceptString = () => {
    switch (acceptedFileTypes) {
      case "pdf":
        return { "application/pdf": [".pdf"] };
      case "image":
        return { "image/*": [] };
      default:
        return {
          "application/pdf": [".pdf"],
          "application/msword": [".doc", ".docx"],
          "application/vnd.ms-excel": [".xls", ".xlsx"],
          "application/vnd.ms-powerpoint": [".ppt", ".pptx"],
          "image/*": [],
        };
    }
  };

  // Handle file drop
  const onDrop = useCallback(
    (acceptedFiles) => {
      const newFiles = acceptedFiles.map((file) => ({
        file,
        preview: URL.createObjectURL(file), // Generate preview URL
      }));

      // Limit total files to maxFiles
      const updatedFiles = [...files, ...newFiles].slice(0, maxFiles);
      setFiles(updatedFiles);

      if (onChange) {
        onChange(updatedFiles);
      }
    },
    [files, maxFiles, onChange]
  );

  const { getRootProps, getInputProps } = useDropzone({
    onDrop,
    accept: getAcceptString(),
    multiple: maxFiles > 1,
  });

  // Remove a file
  const removeFile = (index) => {
    setFiles((prevFiles) => prevFiles.filter((_, i) => i !== index));
  };

  return (
    <div className="flex flex-col items-center justify-center border-2 border-dashed border-[#313131] rounded-lg p-6 w-full">
      {/* Hidden input for manual file selection */}
      <input
        {...getInputProps()}
        ref={fileInputRef}
        style={{ display: "none" }}
      />

      {files.length === 0 ? (
        <div {...getRootProps()} className="cursor-pointer text-center">
          <div className="text-white flex justify-center text-6xl mb-4">
            <BiCloudUpload />
          </div>
          <p className="text-white text-lg font-semibold">{title}</p>
          <p className="text-white text-sm mb-4">{subtitle}</p>
          <SecondaryButton
            onClick={() => fileInputRef.current?.click()}
            className="bg-amber-500 w-[10rem] hover:bg-amber-600 hover:text-white border-none h-[3rem] text-slate-900 justify-center mt-4 transition-transform duration-200 ease-in-out hover:scale-95 active:scale-95"
          >
            Upload File
          </SecondaryButton>
        </div>
      ) : (
        <div className="w-full">
          <h3 className="text-white text-lg font-semibold mb-2">
            Selected Files:
          </h3>
          <ul className="space-y-2">
            {files.map((fileObj, index) => (
              <li
                key={index}
                className="flex items-center justify-between border-2 p-2 rounded-lg"
              >
                <a
                  href={fileObj.preview}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-white hover:underline"
                >
                  {fileObj.file.name}
                </a>
                <button
                  onClick={() => removeFile(index)}
                  className="text-red-500 hover:text-red-600"
                >
                  <BiX size={24} />
                </button>
              </li>
            ))}
          </ul>

          {/* Allow adding more files if limit not reached */}
          {files.length < maxFiles && (
            <p className="text-white text-sm mt-2">
              You can drop more files as long as it doesn't exceed {maxFiles}{" "}
              file{maxFiles > 1 ? "s" : ""}.
            </p>
          )}

          <div className="mt-4 flex justify-between">
            {files.length < maxFiles && (
              <SecondaryButton
                onClick={() => fileInputRef.current?.click()}
                className="bg-amber-500 w-[10rem] hover:bg-amber-600 hover:text-white border-none h-[3rem] text-slate-900 justify-center mt-4 transition-transform duration-200 ease-in-out hover:scale-95 active:scale-95"
              >
                Add More Files
              </SecondaryButton>
            )}

            <SecondaryButton
              onClick={() => setFiles([])}
              className="bg-rose-500 w-[10rem] hover:bg-rose-600 hover:text-white border-none h-[3rem] text-slate-900 justify-center mt-4 transition-transform duration-200 ease-in-out hover:scale-95 active:scale-95"
            >
              Clear All
            </SecondaryButton>
          </div>
        </div>
      )}
    </div>
  );
};

export default FileComponent;
